import React, { useState, useEffect, useRef } from 'react';
import QateMark from './QateMark';
import './HowItWorks.css';

const HowItWorks = () => {
  const [activeStage, setActiveStage] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const stages = [
    {
      id: 0,
      label: 'Discover',
      color: '#3b82f6',
      title: 'Intelligent Discovery',
      description: 'AI analyzes your frontend and backend repositories to understand your application structure. Then actively explores your UI, discovering real user workflows.',
      highlight: 'Scans repos & explores UI'
    },
    {
      id: 1,
      label: 'Generate',
      color: '#3b82f6',
      title: 'Automatic Test Generation',
      description: 'Based on code analysis and UI exploration, Qate generates 30-40 real, executable tests covering your critical user journeys.',
      highlight: '30-40 tests automatically'
    },
    {
      id: 2,
      label: 'Run',
      color: '#3b82f6',
      title: 'Execute & Validate',
      description: 'Run tests on your infrastructure or ours. Review results through conversation, refine tests, and export as standard Playwright scripts.',
      highlight: 'Your infra or ours'
    },
    {
      id: 3,
      label: 'Analyze',
      color: '#ec4899',
      title: 'Bug Analysis & Healing',
      description: 'When tests fail, AI analyzes the root cause - whether it\'s a UI change needing test healing or an actual bug in your code. One-click healing for test maintenance.',
      highlight: 'Smart failure analysis'
    },
    {
      id: 4,
      label: 'Fix',
      color: '#ec4899',
      title: 'Automated Bugfixes',
      description: 'AI suggests precise bugfixes with full context - code snippets, root cause analysis, and implementation guidance. Optionally create GitHub or GitLab issues, or assign to Copilot or Duo for automated PRs.',
      highlight: 'AI-powered bugfixes'
    }
  ];

  // Visibility observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-rotate stages when visible
  useEffect(() => {
    if (!isVisible) return;

    // Last step (Fix) displays longer
    const delay = activeStage === 4 ? 6000 : 4000;
    const timeout = setTimeout(() => {
      setActiveStage(prev => (prev + 1) % 5);
    }, delay);

    return () => clearTimeout(timeout);
  }, [isVisible, activeStage]);

  const activeContent = stages[activeStage];

  return (
    <section className="how-it-works" id="how-it-works" ref={sectionRef}>
      <div className="how-it-works-container">
        <div className="section-header">
          <h2>How Qate AI Works</h2>
          <p className="section-subtitle">From discovery to automated bugfixes in five simple steps</p>
        </div>

        <div className="workflow-layout">
          {/* Wheel Animation */}
          <div className="workflow-wheel-wrapper">
            <div className="workflow-wheel">
              {/* Circle track */}
              <div className="wheel-track" />

              {/* Rotating highlight arc */}
              <div
                className="wheel-highlight"
                style={{
                  transform: `rotate(${activeStage * 72}deg)`,
                  borderTopColor: activeStage >= 3 ? '#ec4899' : '#3b82f6'
                }}
              />

              {/* Center hub with logo — rotates to point at the active stage */}
              <div className="wheel-hub">
                <QateMark
                  className="wheel-logo"
                  circleWidth={3.2}
                  style={{ color: '#1a1a2e', transform: `rotate(${activeStage * 72 + 45}deg)` }}
                />
              </div>

              {/* Stage nodes */}
              {stages.map((stage, index) => {
                const isActive = activeStage === index;
                return (
                  <button
                    key={stage.id}
                    className={`wheel-stage stage-${index} ${isActive ? 'active' : ''}`}
                    onClick={() => setActiveStage(index)}
                    aria-label={stage.label}
                  >
                    <div
                      className="stage-dot"
                      style={{ background: isActive ? stage.color : '#e5e7eb' }}
                    />
                    <span
                      className="stage-label"
                      style={{ color: isActive ? stage.color : '#9ca3af' }}
                    >
                      {stage.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content Panel */}
          <div className="workflow-content">
            <div className="content-card" key={activeStage}>
              <div className="content-header">
                <span
                  className="step-number"
                  style={{
                    background: activeStage >= 3
                      ? 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)'
                      : 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)'
                  }}
                >
                  {activeStage + 1}
                </span>
                <h3>{activeContent.title}</h3>
              </div>
              <span className="content-highlight">{activeContent.highlight}</span>
              <p>{activeContent.description}</p>
            </div>

            {/* Progress indicators */}
            <div className="progress-dots">
              {stages.map((stage, index) => (
                <button
                  key={stage.id}
                  className={`progress-dot ${activeStage === index ? 'active' : ''}`}
                  onClick={() => setActiveStage(index)}
                  aria-label={`Step ${index + 1}: ${stage.label}`}
                  style={{
                    background: activeStage === index ? stage.color : '#e5e7eb'
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Platform Support */}
        <div className="platform-support">
          <div className="platform-badge">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="3" width="20" height="14" rx="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
            <span>Web Apps</span>
          </div>
          <div className="platform-badge">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="3" width="20" height="14" rx="2" />
              <path d="M8 21h8" />
              <path d="M12 17v4" />
              <path d="M7 8h2M7 12h4" />
            </svg>
            <span>Windows Desktop</span>
          </div>
          <div className="platform-badge">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="2" />
              <circle cx="6" cy="6" r="2" />
              <circle cx="18" cy="6" r="2" />
              <circle cx="6" cy="18" r="2" />
              <circle cx="18" cy="18" r="2" />
              <line x1="12" y1="10" x2="12" y2="4" />
              <line x1="10.5" y1="11" x2="7.5" y2="7.5" />
              <line x1="13.5" y1="11" x2="16.5" y2="7.5" />
              <line x1="10.5" y1="13" x2="7.5" y2="16.5" />
              <line x1="13.5" y1="13" x2="16.5" y2="16.5" />
            </svg>
            <span>REST & SOAP APIs</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
